import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Playlist, ScheduleType } from "../../types/index";
import { getRotatedItems } from "../../utils/rotationUtils";
import { PrayerTimes } from "../../types/PrayerTypes";
import { PrayerState } from "../../hooks/usePrayerStateManager";
import { useEnhancedPlaylistScheduler } from "../../hooks/useEnhancedPlaylistScheduler";
import { ScreenData, shouldShowCategory } from "../../types/ScreenTypes";
import "./SimpleEmbeddedSlideshowComponent.css";
import { DigitalAssetType } from "../../types/DigitalAssetTypes";
import SimpleUpcomingSalahChangesSlideshow from "./SimpleUpcomingSalahChangesSlideshow";
import SimpleUpcomingEventsWidget from "./SimpleUpcomingEventsWidget";
import LabbaikSlide from "../slideshow/LabbaikSlide";
import useLabbaikItems, { labbaikSlideshowItems } from "../../hooks/useLabbaikItems";
import CachedImage from "../shared/CachedImage";
import VideoPlayerFactory from "../shared/VideoPlayerFactory";
import { fetchNext7DaysPrayerTimes } from "../../services/api";
import {
  getUrgentInjectCadence,
  mergeInjectItems,
  sortByPriorityDesc,
  splitUrgentInjectPlaylists,
} from "../../utils/urgentInjectUtils";
import { parseVideoUrl } from "../../utils/videoUtils";

interface SimpleEmbeddedSlideshowComponentProps {
  playlists: Playlist[];
  prayerTimes: PrayerTimes;
  tomorrowPrayerTimes?: PrayerTimes | null;
  screenData: ScreenData;
  onComplete: () => void;
  forceContinuousLoop?: boolean;
  prayerState: PrayerState;
  prevFridayJummah1: string | null;
  prevFridayJummah2: string | null;
  prevFridayJummah3: string | null;
  prevFridayJummah4: string | null;
  restartTrigger?: number;
  width?: number;
  height?: number;
}

export default function SimpleEmbeddedSlideshowComponent({
  playlists,
  prayerTimes,
  tomorrowPrayerTimes,
  screenData,
  onComplete,
  forceContinuousLoop = false,
  prayerState,
  prevFridayJummah1,
  prevFridayJummah2,
  prevFridayJummah3,
  prevFridayJummah4,
  restartTrigger = 0,
  width,
  height,
}: SimpleEmbeddedSlideshowComponentProps) {
  // Transform scheduleType from string to enum value (memoized)
  const transformedPlaylists = useMemo(
    () =>
      playlists.map((p) => ({
        ...p,
        scheduleType:
          typeof p.scheduleType === "string"
            ? ScheduleType[
                p.scheduleType as unknown as keyof typeof ScheduleType
              ]
            : p.scheduleType,
      })),
    [playlists],
  );

  // Configure scheduler behavior
  const schedulerConfig = useMemo(
    () => ({
      enablePriorityOverride: true,
      enableInterruption: prayerState.shouldPauseContent,
      defaultMinimumRunDuration: 1, // 1 minute
      defaultMaximumRunDuration: 60, // 1 hour
      evaluationInterval: 30000, // 30 seconds
      enableLogging: process.env.NODE_ENV === "development",
    }),
    [prayerState.shouldPauseContent],
  );

  const {
    schedulerResult,
    isLoading: schedulerLoading,
    error,
  } = useEnhancedPlaylistScheduler({
    playlists: transformedPlaylists as any,
    prayerTimes,
    screenData, // Pass screenData for prayer-specific lengths
    enableAdvancedScheduling: true,
    configuration: schedulerConfig,
  });

  const labbaikItems = useLabbaikItems(screenData?.masjidId, screenData?.showLabbaik);

  // ✅ FIX: Extract actual playlists from scheduler wrapper objects
  const activePlaylists = useMemo(() => {
    if (!schedulerResult?.activePlaylists) return [];

    const extracted = schedulerResult.activePlaylists.map((ap: any) => {
      const playlist = ap.playlist || ap;

      // Also transform items to have nested asset object (same as DisplayController)
      if (playlist.items) {
        playlist.items = playlist.items.map((item: any) => ({
          ...item,
          asset: item.asset || {
            id: item.assetId,
            title: item.assetTitle,
            type: item.assetType,
            url: item.assetUrl,
          },
        }));
      }

      return playlist;
    });

    return extracted;
  }, [schedulerResult?.activePlaylists]);

  // Slideshow state
  const [allItems, setAllItems] = useState<any[]>([]);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [displayMode, setDisplayMode] = useState<
    "contain" | "cover" | "fill" | "blur"
  >(
    screenData.noticeboardSettings?.slideshowPosterFitMode === "cover"
      ? "cover"
      : screenData.noticeboardSettings?.slideshowPosterFitMode === "fill"
        ? "fill"
        : screenData.noticeboardSettings?.slideshowPosterFitMode === "blur"
          ? "blur"
          : "contain",
  );
  const slideshowPosterFitMode =
    screenData.noticeboardSettings?.slideshowPosterFitMode ?? "auto";

  const timerRef = useRef<number | null>(null);
  const intervalRef = useRef<number | null>(null);
  const onCompleteRef = useRef(onComplete);
  const itemsSnapshotRef = useRef<any[] | null>(null);
  const cycleActiveRef = useRef(false);

  const stableRefs = useRef({
    // Store stable references to callbacks
    handleVideoEnded: null as any,
  });

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Initialize stable refs on first render
  useEffect(() => {
    stableRefs.current.handleVideoEnded = (event: any) => {
      // Clear any existing timers
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // Add a small delay to ensure DOM operations complete
      setTimeout(() => {
        const nextIndex = currentItemIndex + 1;
        const items = itemsSnapshotRef.current || [];
        if (nextIndex >= items.length) {
          cycleActiveRef.current = false;
          onCompleteRef.current?.();
        } else {
          startItemTimer(nextIndex);
        }
      }, 200);
    };
  }, []);

  // Handle restart trigger
  useEffect(() => {
    if (restartTrigger > 0) {
      // Clear any existing timers
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // Reset cycle state and restart
      cycleActiveRef.current = false;
      setCurrentItemIndex(0);
      setElapsedTime(0);
    }
  }, [restartTrigger]);

  // ✅ Fetch 7-day timetable using existing API infrastructure
  const [timetables, setTimetables] = useState<PrayerTimes[]>([]);
  const [isFetchingTimetables, setIsFetchingTimetables] = useState(false);

  // Build slideshow items from active playlists
  useEffect(() => {
    // Only use activePlaylists when scheduler is ready
    const sourcePlaylists =
      activePlaylists && activePlaylists.length > 0 && !schedulerLoading
        ? activePlaylists
        : [];

    const now = new Date();
    const { regularPlaylists, injectPlaylists } = splitUrgentInjectPlaylists(
      sourcePlaylists,
      now,
    );

    const regularItems: any[] = [];
    const injectItems: any[] = [];

    const sortedRegular = sortByPriorityDesc(regularPlaylists);

    for (const pl of sortedRegular) {
      if (!pl?.items?.length) {
        continue;
      }
      let playlistItems = pl.items
        .slice()
        .sort(
          (a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0),
        );

      // Rotation mode: daily for date-based, weekly for time/prayer-based schedules
      // When enabled, shows only one item per rotation period using modulo
      if (pl.isDailyRotation && playlistItems.length > 0) {
        playlistItems = getRotatedItems(
          pl,
          playlistItems,
          prayerTimes,
          "[SimpleEmbeddedSlideshow]",
        );
      }

      // Filter by poster category (showEventPosters, showFuneralNotices, showInformationPosters)
      playlistItems = playlistItems.filter((item: any) => {
        // Skip special items (widgets, etc.) - they don't have categories
        if (
          item.isSpecialItem ||
          item.assetType === "upcomingSalahChanges" ||
          item.assetType === "upcomingEventsWidget"
        ) {
          return true;
        }
        const category = item.eventCategory || "Event";
        const shouldShow = shouldShowCategory(screenData, category);
        if (!shouldShow) {
          console.log(
            `[SimpleEmbeddedSlideshow] Filtering out item "${item.assetTitle}" with category "${category}"`,
          );
        }
        return shouldShow;
      });
      regularItems.push(...playlistItems);
    }

    const sortedInject = sortByPriorityDesc(injectPlaylists);

    for (const pl of sortedInject) {
      if (!pl?.items?.length) {
        continue;
      }

      const sortedItems = pl.items
        .slice()
        .sort(
          (a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0),
        );

      const filteredItems = sortedItems.filter((item: any) => {
        if (
          item.isSpecialItem ||
          item.assetType === "upcomingSalahChanges" ||
          item.assetType === "upcomingEventsWidget"
        ) {
          return true;
        }

        const category = item.eventCategory || "Event";
        const shouldShow = shouldShowCategory(screenData, category);
        if (!shouldShow) {
          console.log(
            `[SimpleEmbeddedSlideshow] Filtering out inject item "${item.assetTitle}" with category "${category}"`,
          );
        }
        return shouldShow;
      });

      injectItems.push(
        ...filteredItems.map((item: any) => ({
          ...item,
          duration: Math.max(
            item.duration || 10,
            pl.urgentMinShowSeconds || 10,
          ),
          isUrgentInjectItem: true,
        })),
      );
    }

    const injectCadence = getUrgentInjectCadence(sortedInject);
    const items = mergeInjectItems(regularItems, injectItems, injectCadence);
    items.push(...labbaikSlideshowItems(labbaikItems));

    // ✅ FIX: Only add upcoming-salah-changes if we have timetable data
    // This prevents showing "No upcoming changes" slide when there's no data
    if (
      screenData.showNextSalahChanges &&
      timetables.length >= 2 // Need at least 2 days to detect changes
    ) {
      items.push({
        id: "upcoming-salah-changes",
        assetType: "upcomingSalahChanges",
        duration: screenData.nextSalahChangesDuration || 10,
        displayOrder: 999,
        isSpecialItem: true,
      });
    }

    // Add upcoming events widget if enabled
    console.log("[SimpleEmbeddedSlideshow] showUpcomingEvents check:", {
      showUpcomingEvents: screenData.showUpcomingEvents,
      upcomingEventsDuration: screenData.upcomingEventsDuration,
      itemsBeforeAdd: items.length,
    });
    if (screenData.showUpcomingEvents) {
      items.push({
        id: "upcoming-events-widget",
        assetType: "upcomingEventsWidget",
        duration: screenData.upcomingEventsDuration || 15,
        displayOrder: 998,
        isSpecialItem: true,
      });
      console.log(
        "[SimpleEmbeddedSlideshow] Added upcoming events widget, total items:",
        items.length,
      );
    }

    // Check if items actually changed
    const itemsChanged =
      !itemsSnapshotRef.current ||
      itemsSnapshotRef.current.length !== items.length ||
      itemsSnapshotRef.current.some(
        (oldItem, index) => !items[index] || oldItem.id !== items[index].id,
      );

    itemsSnapshotRef.current = items;
    setAllItems(items);

    // Only set loading to false if we have items or if scheduler is done loading
    if (items.length > 0) {
      setIsLoading(false);
    } else if (!schedulerLoading) {
      setIsLoading(false);
    }

    // Only reset cycle state and item index if items actually changed
    if (itemsChanged) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      cycleActiveRef.current = false;
      setCurrentItemIndex(0);
    }
  }, [
    activePlaylists,
    schedulerLoading,
    screenData.showNextSalahChanges,
    screenData.nextSalahChangesDuration,
    screenData.showUpcomingEvents,
    screenData.upcomingEventsDuration,
    labbaikItems,
    timetables, // ✅ Depend on timetables instead of individual prayer times
    prayerTimes?.HijriDateComponents, // ✅ Re-evaluate when Hijri date changes (for daily rotation)
  ]);

  // Start timers once items ready
  useEffect(() => {
    if (
      isLoading ||
      schedulerLoading ||
      !itemsSnapshotRef.current ||
      !itemsSnapshotRef.current.length ||
      prayerState.shouldPauseContent ||
      prayerState.isCountdownActive
    )
      return;

    // ensure we start a new cycle only once
    if (!cycleActiveRef.current) {
      cycleActiveRef.current = true;
      startItemTimer(0);
    }

    return () => {
      // cleanup on unmount or re-run
      cycleActiveRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [
    isLoading,
    schedulerLoading,
    prayerState.shouldPauseContent,
    prayerState.isCountdownActive,
    restartTrigger,
  ]);

  // Pause/resume
  useEffect(() => {
    const paused =
      prayerState.shouldPauseContent || prayerState.isCountdownActive;
    if (paused) {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    } else if (allItems.length && !timerRef.current) {
      startItemTimer(currentItemIndex);
    }
  }, [
    prayerState.shouldPauseContent,
    prayerState.isCountdownActive,
    allItems.length,
  ]);

  // Return to prayer view if no items
  useEffect(() => {
    if (
      !isLoading &&
      !schedulerLoading &&
      allItems.length === 0 &&
      !screenData.showNextSalahChanges
    ) {
      const h = setTimeout(() => {
        onCompleteRef.current && onCompleteRef.current();
      }, 0);
      return () => clearTimeout(h);
    }
  }, [
    isLoading,
    schedulerLoading,
    allItems.length,
    screenData.showNextSalahChanges,
  ]);

  // Progress bar (smooth)
  useEffect(() => {
    if (isLoading || allItems.length === 0) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    setElapsedTime(0);
    intervalRef.current = window.setInterval(() => {
      setElapsedTime((t) => t + 0.1);
    }, 100);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [currentItemIndex, isLoading, allItems.length]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Stable snapshot-driven timer function
  function moveToNextOrComplete(nextIndex: number, items: any[]) {
    if (nextIndex >= items.length) {
      if (forceContinuousLoop && items.length > 0) {
        startItemTimer(0);
      } else {
        cycleActiveRef.current = false;
        onCompleteRef.current?.();
      }
      return;
    }

    startItemTimer(nextIndex);
  }

  function startItemTimer(index: number) {
    // clear any previous timers
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    const items = itemsSnapshotRef.current || [];
    if (!items.length) {
      onCompleteRef.current?.();
      return;
    }

    // If index beyond end -> either loop or complete
    if (index >= items.length) {
      moveToNextOrComplete(index, items);
      return;
    }

    const item = items[index];
    setCurrentItemIndex(index);

    // Check if this is a video
    const isVideo = item.assetType?.toLowerCase() === "video";
    const isEmbeddedVideo =
      item.videoType === "embedded" ||
      parseVideoUrl(item.assetUrl || "").platform === "embedded";
    const isSingleEmbeddedItem = isEmbeddedVideo && items.length === 1;

    // For videos, don't set a timer - let onEnded callback handle it
    if (isVideo) {
      setElapsedTime(0);
      // Set up a display-only progress bar
      const videoDuration = item.duration || 30;
      // Start progress tick for UI display only
      intervalRef.current = window.setInterval(() => {
        setElapsedTime((t) => {
          // Cap at 99% to show progress is happening but not complete
          return t + 0.1 > videoDuration ? videoDuration * 0.99 : t + 0.1;
        });
      }, 100);

      // Keep a single embedded item mounted continuously (e.g., CCTV iframe).
      if (!isSingleEmbeddedItem) {
        // Set a failsafe timer in case video events don't fire.
        const failsafeTime = Math.max(videoDuration + 10, 40); // At least 40 seconds or video duration + 10s
        timerRef.current = window.setTimeout(() => {
          // Check if we're still on the same item
          if (currentItemIndex === index) {
            handleVideoEnded();
          }
        }, failsafeTime * 1000);
      }

      return;
    }

    // For non-video items:
    const duration = Math.max(1, item?.duration || 10);
    setElapsedTime(0);
    // start progress tick (for UI)
    intervalRef.current = window.setInterval(() => {
      setElapsedTime((t) => t + 0.1);
    }, 100);
    // Handle special cases
    const isLast = index === items.length - 1;
    const isUpcoming = item.assetType === "upcomingSalahChanges";
    timerRef.current = window.setTimeout(() => {
      // cleanup interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (isLast && isUpcoming) {
        moveToNextOrComplete(index + 1, items);
      } else {
        // move to next item in snapshot
        startItemTimer(index + 1);
      }
    }, duration * 1000);
  }

  const currentItem = allItems[currentItemIndex];

  // Add ref for last loaded asset URL
  const handleVideoEnded = useCallback(() => {
    // Set a flag to avoid double-handling
    const isHandling = { current: true };
    // Clear any existing timers
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    // Add a small delay to ensure DOM operations complete
    setTimeout(() => {
      // Only proceed if we haven't been cleaned up
      if (!isHandling.current) return;
      const nextIndex = currentItemIndex + 1;
      const items = itemsSnapshotRef.current || [];
      moveToNextOrComplete(nextIndex, items);
    }, 200); // Slightly longer delay for more reliable DOM updates
    return () => {
      isHandling.current = false;
    };
  }, [currentItemIndex]);

  if (schedulerLoading || isLoading)
    return <div className="embedded-slideshow-loading">Loading...</div>;

  if (!allItems.length) return null;

  if (error)
    return <div className="embedded-slideshow-error">Error: {error}</div>;

  const currentItemDuration = currentItem?.duration || 10;

  if (currentItem && currentItem.assetType === "upcomingSalahChanges") {
    return (
      <SimpleUpcomingSalahChangesSlideshow
        timetables={timetables} // ✅ Now properly fetched
        customizedNames={screenData.customizedNames}
        width={width}
        height={height}
        duration={currentItem.duration || 10}
        onComplete={() => {
          const nextIndex = currentItemIndex + 1;
          const items = itemsSnapshotRef.current || [];
          moveToNextOrComplete(nextIndex, items);
        }}
        prayerTimes={prayerTimes!}
        tomorrowPrayerTimes={tomorrowPrayerTimes || null}
        screenData={screenData}
        prevFridayJummah1={prevFridayJummah1}
        prevFridayJummah2={prevFridayJummah2}
        prevFridayJummah3={prevFridayJummah3}
        prevFridayJummah4={prevFridayJummah4}
        currentIndex={currentItemIndex}
        totalItems={allItems.length}
        elapsedTime={elapsedTime}
        totalDuration={currentItemDuration}
      />
    );
  }

  // Render upcoming events widget
  if (currentItem && currentItem.assetType === "upcomingEventsWidget") {
    return (
      <SimpleUpcomingEventsWidget
        masjidId={screenData?.masjidId}
        duration={currentItem.duration}
        onComplete={() => {
          const nextIndex = currentItemIndex + 1;
          const items = itemsSnapshotRef.current || [];
          moveToNextOrComplete(nextIndex, items);
        }}
        screenData={screenData}
        primaryColor={screenData.primaryColor}
        secondaryColor={screenData.secondaryColor}
        accentColor={screenData.accentColor}
        currentIndex={currentItemIndex}
        totalItems={allItems.length}
        elapsedTime={elapsedTime}
        totalDuration={currentItemDuration}
        restartTrigger={restartTrigger}
      />
    );
  }

  const assetType = currentItem.assetType?.toLowerCase() || "";
  const keepAliveEmbedded =
    assetType === DigitalAssetType.Video &&
    (currentItem.videoType === "embedded" ||
      parseVideoUrl(currentItem.assetUrl || "").platform === "embedded") &&
    allItems.length === 1;

  const progress = Math.min((elapsedTime / currentItemDuration) * 100, 100);

  // Custom CSS variables for theming
  const customStyles = {
    "--compact-primary-color": screenData.primaryColor || "#FFC107",
  } as React.CSSProperties;

  return (
    <div className="embedded-slideshow-component" style={customStyles}>
      <div className="embedded-slideshow-content">
        {assetType === DigitalAssetType.Poster ? (
          <div className="embedded-slideshow-item image-item">
            {displayMode === "blur" && (
              // The poster at its true shape over a blurred, darkened copy of itself
              <CachedImage
                src={currentItem.assetUrl}
                alt=""
                className="embedded-slideshow-backdrop"
                assetType={assetType}
              />
            )}
            {
              <CachedImage
                src={currentItem.assetUrl}
                alt={currentItem.assetTitle || "Embedded Slideshow Image"}
                className={`embedded-slideshow-image${
                  displayMode === "fill"
                    ? " fill-mode"
                    : displayMode === "cover"
                      ? " stretch-mode"
                      : ""
                }`}
                assetType={assetType}
                onLoad={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                  const img = e.currentTarget;
                  const aspectRatio = img.naturalWidth / img.naturalHeight;
                  const SQUARE_THRESHOLD = 0.1;
                  let orient: "landscape" | "portrait" | "square";
                  if (Math.abs(aspectRatio - 1) < SQUARE_THRESHOLD) {
                    orient = "square";
                  } else if (aspectRatio > 1) {
                    orient = "landscape";
                  } else {
                    orient = "portrait";
                  }
                  if (slideshowPosterFitMode !== "auto") {
                    setDisplayMode(
                      slideshowPosterFitMode as
                        | "contain"
                        | "cover"
                        | "fill"
                        | "blur",
                    );
                  } else {
                    // Orientation-aware fitting fallback:
                    // Same orientation as the slot → fill (slight stretch, no bars, no cropping)
                    // Different orientation or square → contain (bars accepted)
                    // Compare with the slot itself: a portrait screen's slot can be landscape-shaped.
                    const slotWidth = width ?? window.innerWidth;
                    const slotHeight = height ?? window.innerHeight;
                    const slotOrient =
                      slotHeight > slotWidth ? "portrait" : "landscape";
                    if (orient === "square") {
                      setDisplayMode("contain");
                    } else if (orient === slotOrient) {
                      setDisplayMode("fill");
                    } else {
                      setDisplayMode("contain");
                    }
                  }
                }}
                // belt-and-braces inline style
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: displayMode === "blur" ? "contain" : displayMode,
                  objectPosition: "center",
                }}
              />
            }
          </div>
        ) : assetType === DigitalAssetType.Video ? (
          <div className="embedded-slideshow-item video-item">
            <div className="embedded-video-container">
              <VideoPlayerFactory
                // Remove Date.now() from key to prevent constant remounting
                key={`embedded-video-${
                  currentItem.id || currentItem.assetId || currentItemIndex
                }-${restartTrigger}`}
                playbackCycle={restartTrigger}
                keepAliveEmbedded={keepAliveEmbedded}
                item={{
                  ...currentItem,
                  // Explicitly set required properties
                  id: currentItem.id || `embedded-video-${currentItemIndex}`,
                  assetId:
                    currentItem.assetId ||
                    currentItem.id ||
                    `asset-${currentItemIndex}`,
                  assetUrl: currentItem.assetUrl,
                  assetTitle: currentItem.assetTitle || "Embedded Video",
                  assetType: "video",
                  duration: currentItem.duration || 30,
                  videoAutoplay: currentItem.videoAutoplay !== false,
                  videoMuted: currentItem.videoMuted !== false,
                  videoLoop: currentItem.videoLoop === true,
                }}
                onEnded={handleVideoEnded}
                onError={() => {
                  // Use timeout to ensure DOM operations complete
                  setTimeout(() => handleVideoEnded(), 100);
                }}
                className="embedded-slideshow-video-player"
                fillMode="contain"
                screenData={{
                  showPortraitPosterOnLandscape:
                    screenData.showPortraitPosterOnLandscape,
                  showLandscapePosterOnPortrait:
                    screenData.showLandscapePosterOnPortrait,
                  orientation: screenData.orientation,
                }}
              />
            </div>
          </div>
        ) : assetType === "labbaik" ? (
          <div className="embedded-slideshow-item labbaik-item">
            <LabbaikSlide
              item={currentItem.labbaik}
              colours={screenData}
            />
          </div>
        ) : assetType === "html" ? (
          <div className="embedded-slideshow-item html-item">
            <div
              className="embedded-slideshow-html-content"
              dangerouslySetInnerHTML={{ __html: currentItem.assetUrl }}
            />
          </div>
        ) : (
          <div className="embedded-slideshow-item unknown-item">
            <div className="embedded-unknown-content">
              Unknown content type: {currentItem.assetType}
            </div>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="simple-embedded-progress">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className="embedded-slideshow-footer">
        <div className="embedded-slideshow-counter">
          {currentItemIndex + 1} / {allItems.length}
        </div>
      </div>
    </div>
  );
}

function getTotalSlideshowDuration(items: any[]): number {
  return items.reduce((t, i) => t + (i.duration || 10), 0);
}
